import React, { useState } from 'react';
import { Evaluation } from '../App';
import MarkdownRenderer from './MarkdownRenderer';

interface RoastPanelProps {
  evaluation: Evaluation | null;
  isOpen: boolean;
  onClose: () => void;
}

const RoastPanel: React.FC<RoastPanelProps> = ({ evaluation, isOpen, onClose }) => {
  if (!evaluation) return null;

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <>
      <div 
        className={`fixed inset-0 bg-black/50 z-[999] transition-all duration-300 ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`} 
        onClick={onClose} 
      />
      <div className={`fixed top-0 h-screen bg-neutral-darker border-l border-neutral-light z-[1000] overflow-y-auto transition-all duration-300 ${isOpen ? 'right-0' : '-right-full'} w-full sm:w-[90%] md:w-[60%] lg:w-[55%] xl:w-[50%] font-serif`}>
        <button 
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded text-critique-primary text-2xl hover:bg-neutral-gray transition-all"
          onClick={onClose}
        >
          ×
        </button>
        
        <div className="p-6 md:p-12 pt-20">
          <h2 className="text-critique-primary text-2xl md:text-3xl font-display font-bold mb-6 leading-tight">
            {evaluation.paper_title || `Paper ${evaluation.arxiv_paper_id}`}
          </h2>
          
          <div className="bg-neutral-gray/50 rounded-lg p-4 md:p-6 mb-8 border border-neutral-light">
            <p className="text-neutral-lighter mb-3">
              <span className="text-critique-primary font-semibold">Authors:</span> {evaluation.paper_authors || 'Unknown'}
            </p>
            <p className="text-neutral-lighter mb-3">
              <span className="text-critique-primary font-semibold">Paper:</span>{' '}
              <a 
                href={evaluation.paper_url || `https://arxiv.org/abs/${evaluation.arxiv_paper_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-critique-primary hover:underline"
              >
                {evaluation.arxiv_paper_id}
              </a>
            </p>
            <p className="text-neutral-lighter mb-3">
              <span className="text-critique-primary font-semibold">Analyzed on:</span> {formatDate(evaluation.created_at)}
            </p>
            <p className="text-neutral-lighter mb-3">
              <span className="text-critique-primary font-semibold">Analysis type:</span> {evaluation.prompt_type}
            </p>
            {evaluation.prompt_style && (
              <p className="text-neutral-lighter mb-3">
                <span className="text-critique-primary font-semibold">Style:</span> {evaluation.prompt_style}
              </p>
            )}
            <div className="flex gap-2 mt-4">
              <a
                href={`/review/${evaluation.evaluation_uuid}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-neutral-gray text-white rounded border border-neutral-light hover:bg-neutral-light transition-all text-sm"
              >
                Direct Link
              </a>
              <button
                onClick={() => {
                  const baseUrl = window.location.origin;
                  const reviewUrl = `${baseUrl}/review/${evaluation.evaluation_uuid}`;
                  const firstParagraph = evaluation.full_evaluation_text.split('\n\n')[0];
                  const tweetReview = firstParagraph.length > 200 
                    ? firstParagraph.substring(0, 197) + '...' 
                    : firstParagraph;
                  const tweetText = encodeURIComponent(`${tweetReview}\n\n@YourResearchSucks ${reviewUrl}`);
                  window.open(`https://x.com/intent/tweet?text=${tweetText}`, '_blank');
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-all text-sm flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                Tweet
              </button>
            </div>
          </div>
          
          {evaluation.paper_abstract && (
            <div className="mb-8">
              <h3 className="text-critique-primary text-xl md:text-2xl font-display font-bold mb-4">Abstract</h3>
              <p className="text-neutral-lighter leading-relaxed italic">{evaluation.paper_abstract}</p>
            </div>
          )}

          <div className="mb-8">
            <h3 className="text-critique-primary text-xl md:text-2xl font-display font-bold mb-4">Critical Analysis</h3>
            <div className="bg-neutral-gray/50 border-l-[3px] border-critique-primary p-4 md:p-6 rounded">
              <MarkdownRenderer content={evaluation.full_evaluation_text} />
            </div>
          </div>
          
          <div className="pt-8 border-t border-neutral-light">
            <p className="text-neutral-lighter text-sm">Analysis ID: {evaluation.evaluation_uuid}</p>
            <div className="mt-6">
              <a 
                href="/make-your-research-better" 
                className="inline-block px-6 py-3 bg-neutral-gray text-critique-primary border border-critique-primary rounded hover:bg-critique-primary hover:text-white transition-all"
              >
                Make Your Research Better →
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RoastPanel;
